import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiCalendar, FiSearch, FiPackage, FiFileText, FiTruck, FiUser, FiShoppingCart } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useCompras } from '../../hooks/useCompras';
import { proveedoresService, productosService } from '../../services';

const CompraForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  const { crearOrden, actualizarOrden, getOrdenById } = useCompras();

  // Estados del formulario
  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    priority: 'NORMAL',
    notes: '',
    supplier_notes: '',
    shipping_cost: 0,
    discount_amount: 0,
    tax_rate: 0,
    items: []
  });

  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);

  // Cargar datos iniciales
  useEffect(() => {
    loadProveedores();
    loadProductos();

    if (isEditing) {
      loadOrdenData();
    }
  }, [id, isEditing]);

  // Búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      const filtered = productos.filter(product =>
        (product.name || product.nombreProducto || '').toLowerCase().includes(productSearch.toLowerCase()) ||
        (product.code || product.codigo || '').toLowerCase().includes(productSearch.toLowerCase())
      );
      setSearchResults(filtered);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch, productos]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadProveedores = async () => {
    try {
      const result = await proveedoresService.getProveedores({ limit: 100 });
      if (result.success) {
        setProveedores(result.data);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadProductos = async () => {
    try {
      const result = await productosService.getProductos({ limit: 100 });
      if (result.success) {
        setProductos(result.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadOrdenData = async () => {
    setIsLoading(true);
    try {
      const result = await getOrdenById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        setError('Error al cargar la orden de compra');
      }
    } catch (error) {
      setError('Error de conexión al cargar la orden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: typeof value === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.name || product.nombreProducto);
    setSearchResults([]);
    setShowDropdown(false);
  };

  const addItemToOrden = () => {
    if (!selectedProduct || itemQuantity <= 0) {
      setError('Seleccione un producto y especifique una cantidad válida');
      return;
    }

    const newItem = {
      product_id: selectedProduct.id,
      quantity: itemQuantity,
      unit_cost: selectedProduct.cost_price || selectedProduct.costoUnitario || 0,
      description: selectedProduct.name || selectedProduct.nombreProducto,
      notes: '',
      expected_delivery_date: ''
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setError(null);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === 'quantity' || field === 'unit_cost'
                ? parseFloat(value) || 0
                : value
            }
          : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) =>
      sum + (item.quantity * item.unit_cost), 0
    );
    const taxAmount = subtotal * (formData.tax_rate / 100);
    const total = subtotal + taxAmount + formData.shipping_cost - formData.discount_amount;

    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.supplier_id) {
      setError('Debe seleccionar un proveedor');
      return;
    }

    if (formData.items.length === 0) {
      setError('Debe agregar al menos un producto');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const totals = calculateTotals();
      const orderData = {
        ...formData,
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        total_amount: totals.total
      };

      const result = isEditing
        ? await actualizarOrden(id, orderData)
        : await crearOrden(orderData);

      if (result.success) {
        navigate('/system/compras');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Error al guardar la orden de compra');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/system/compras');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getProductName = (productId) => {
    const product = productos.find(p => p.id === parseInt(productId));
    return product ? (product.name || product.nombreProducto) : '';
  };

  const totals = calculateTotals();

  if (isLoading) {
    return <LoadingGrid message="Cargando orden de compra..." />;
  }

  return (
    <div className="factura-form-page compra-form-page">
      {/* Header Premium */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            <FiSave size={16} />
            {isSaving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Orden')}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="factura-form">
        {/* Card 1: Información Principal - Diseño Premium */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiFileText size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">
                {isEditing ? 'Orden #' + id : 'Nueva Orden de Compra'}
              </h3>
              <p className="invoice-header-subtitle">
                Complete los detalles de la orden
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            {/* Proveedor */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Proveedor *</label>
                <select
                  className="invoice-field-input"
                  value={formData.supplier_id}
                  onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                  required
                >
                  <option value="">Seleccionar proveedor...</option>
                  {proveedores.map(proveedor => (
                    <option key={proveedor.id} value={proveedor.id}>
                      {proveedor.name || proveedor.nombreEmpresa}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Prioridad */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Prioridad</label>
                <select
                  className="invoice-field-input"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                >
                  <option value="LOW">Baja</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">Alta</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>
            </div>

            {/* Fecha de Orden */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Orden *</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={formData.order_date}
                  onChange={(e) => handleInputChange('order_date', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Fecha de Entrega Esperada */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiTruck size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Entrega</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={formData.expected_delivery_date}
                  onChange={(e) => handleInputChange('expected_delivery_date', e.target.value)}
                />
              </div>
            </div>

            {/* Notas Internas - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas Internas</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas para uso interno..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows="2"
                />
              </div>
            </div>

            {/* Notas para Proveedor - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas para el Proveedor</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas que se enviarán al proveedor..."
                  value={formData.supplier_notes}
                  onChange={(e) => handleInputChange('supplier_notes', e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Buscador de Productos - Diseño Moderno */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la orden</p>
            </div>
          </div>

          <div className="product-search-body">
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />

              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="product-dropdown">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="product-dropdown-item"
                      onClick={() => handleProductSelect(product)}
                    >
                      <div className="product-dropdown-icon">
                        <FiPackage size={20} />
                      </div>
                      <div className="product-dropdown-info">
                        <div className="product-dropdown-name">
                          {product.name || product.nombreProducto}
                        </div>
                        <div className="product-dropdown-meta">
                          <span className="meta-code">{product.code || product.codigo}</span>
                          <span className="meta-separator">•</span>
                          <span className="meta-stock">Stock: {product.stock || 0}</span>
                        </div>
                      </div>
                      <div className="product-dropdown-price">
                        {formatCurrency(product.cost_price || product.costoUnitario || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">
                      {selectedProduct.name || selectedProduct.nombreProducto}
                    </h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Código: {selectedProduct.code || selectedProduct.codigo}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">
                        Costo: {formatCurrency(selectedProduct.cost_price || selectedProduct.costoUnitario || 0)}
                      </span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Stock: {selectedProduct.stock || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(parseFloat(e.target.value) || 1)}
                      min="0.1"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToOrden}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Tabla de Productos - Diseño Profesional */}
        {formData.items.length > 0 ? (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon">
                <FiShoppingCart size={24} />
              </div>
              <div className="table-header-content">
                <h3 className="table-header-title">Productos en la Orden</h3>
                <p className="table-header-subtitle">{formData.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead className="invoice-table-head">
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-qty">Cantidad</th>
                    <th className="th-price">Costo Unit.</th>
                    <th className="th-total">Total</th>
                    <th className="th-actions"></th>
                  </tr>
                </thead>
                <tbody className="invoice-table-body">
                  {formData.items.map((item, index) => (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">
                              {item.description || getProductName(item.product_id)}
                            </div>
                            <div className="product-cell-code">
                              ID: {item.product_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <input
                          type="number"
                          className="qty-input"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          min="0.01"
                          step="0.01"
                        />
                      </td>
                      <td className="td-price">
                        <input
                          type="number"
                          className="price-input"
                          value={item.unit_cost}
                          onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="td-total">
                        <span className="total-value">
                          {formatCurrency(item.quantity * item.unit_cost)}
                        </span>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-icon-delete"
                          onClick={() => removeItem(index)}
                          title="Eliminar producto"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales y Costos Adicionales */}
            <div className="invoice-totals-section">
              {/* Costos adicionales */}
              <div className="additional-costs">
                <div className="cost-row">
                  <label>Costo de Envío</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.shipping_cost}
                    onChange={(e) => handleInputChange('shipping_cost', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="cost-row">
                  <label>Descuento</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.discount_amount}
                    onChange={(e) => handleInputChange('discount_amount', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="cost-row">
                  <label>Impuesto (%)</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.tax_rate}
                    onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Resumen de totales */}
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Subtotal</span>
                  <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="totals-row">
                  <span className="totals-label">Impuestos</span>
                  <span className="totals-value">{formatCurrency(totals.taxAmount)}</span>
                </div>
                <div className="totals-row">
                  <span className="totals-label">Envío</span>
                  <span className="totals-value">{formatCurrency(formData.shipping_cost)}</span>
                </div>
                {formData.discount_amount > 0 && (
                  <div className="totals-row totals-row--discount">
                    <span className="totals-label">Descuento</span>
                    <span className="totals-value">-{formatCurrency(formData.discount_amount)}</span>
                  </div>
                )}
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Total</span>
                  <span className="totals-value">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="invoice-empty-state">
            <div className="empty-state-icon">
              <FiShoppingCart size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la orden
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default CompraForm;
